"use client";

import { memo, useCallback, useState } from "react";
import type { ChartHeaderProps } from "./types";
import {
  LuClock,
  LuLayers,
  LuPanelTop,
  LuSquareArrowOutUpRight,
} from "react-icons/lu";
import { PiFunction } from "react-icons/pi";
import { KLineChartSymbolSelector } from "@/components/chart/symbol-selector";
import type { TimeFrame } from "@/vybe-trader-client/chart/stores/useChartStore";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn-components";
import { openChartPopup } from "@/lib/chart-popup/openChartPopup";
import { cn } from "@/lib/utils";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useChartPanelId } from "@/components/chart/ChartPanelContext";
import IndicatorsScreen from "../chart-indicators/IndicatorsScreen2";
import DrawingsScreen from "../chart-drawings/DrawingsScreen";

const ALL_TIMEFRAMES: TimeFrame[] = [
  "1m",
  "5m",
  "15m",
  "30m",
  "1h",
  "2h",
  "4h",
  "12h",
  "1d",
  "1w",
  "1mn",
];
// All timeframes ride the (scrollable) chip row on mobile too — hiding 4h/1d behind
// an unlabelled clock icon cost a 3-tap detour on the most-used swing timeframes.
const MOBILE_VISIBLE_TIMEFRAMES: TimeFrame[] = ALL_TIMEFRAMES;
const DESKTOP_VISIBLE_TIMEFRAMES: TimeFrame[] = ALL_TIMEFRAMES; // All timeframes visible

function ChartHeader({
  showIndicatorsScreen,
  isCrosshairVisible,
  setIsCrosshairVisible,
  showObjectsScreen,
  isPanelVisible,
  setIsPanelVisible,
  isSpecificTradePanelVisible,
  setIsSpecificTradePanelVisible,
  isMarketDepthVisible,
  setIsMarketDepthVisible,
  isDesktop = false,
  isPopup = false,
  showIndicatorsPopover = false,
  onIndicatorsPopoverChange,
  showObjectsPopover = false,
  onObjectsPopoverChange,
  selectedObjectType,
  onObjectSelected,
}: ChartHeaderProps) {
  const chartPanelId = useChartPanelId();
  const selectedTimeframe = useChartStore3(
    (state) => state.slots[chartPanelId].selectedTimeframe,
  );
  const selectedSymbol = useChartStore3(
    (state) => state.slots[chartPanelId].selectedSymbol,
  );
  const isPendingPanelActive =
    isSpecificTradePanelVisible && Boolean(selectedSymbol);
  const toggleButtonClass = isDesktop ? "h-5 w-5" : "h-8 w-8";
  const toggleIconSize = isDesktop ? 11 : 13;
  const toolButtonClass = isDesktop ? "h-5 w-5" : "h-8 w-8";
  const toolIconSize = isDesktop ? 11 : 13;
  const chipButtonClass = isDesktop
    ? "h-5 px-1.5 text-[11px]"
    : "h-6 px-2 text-xs";

  const [showTimeframeSelector, setShowTimeframeSelector] = useState(false);
  const [moreTimeframesOpen, setMoreTimeframesOpen] = useState(false);

  const visibleTimeframes = isDesktop
    ? DESKTOP_VISIBLE_TIMEFRAMES
    : MOBILE_VISIBLE_TIMEFRAMES;
  const hiddenTimeframes = isDesktop
    ? []
    : ALL_TIMEFRAMES.filter(
        (tf: TimeFrame) => !MOBILE_VISIBLE_TIMEFRAMES.includes(tf)
      );

  const handleTimeframeSelect = (timeframe: TimeFrame) => {
    useChartStore3.getState().setSelectedTimeframe(timeframe, chartPanelId);
    setShowTimeframeSelector(false);
    setMoreTimeframesOpen(false);
  };

  // Stable handlers for the popover content children (IndicatorsScreen2,
  // DrawingsScreen) so they don't re-render on every ChartHeader render.
  const handleIndicatorsBack = useCallback(
    () => onIndicatorsPopoverChange?.(false),
    [onIndicatorsPopoverChange],
  );
  const handleObjectsBack = useCallback(
    () => onObjectsPopoverChange?.(false),
    [onObjectsPopoverChange],
  );
  const handleObjectSelected = useCallback(
    (type: Parameters<NonNullable<typeof onObjectSelected>>[0]) =>
      onObjectSelected?.(type),
    [onObjectSelected],
  );

  return (
    <div
      className={`shrink-0 flex items-center justify-between ${
        isDesktop ? "px-2 py-0.5 h-8" : "px-2 h-9"
      } bg-background relative`}
    >
      {/* Left side - Timeframe and Symbol */}
      <div className={`flex items-center ${isDesktop ? "gap-2" : "gap-1.5"}`}>
        {/* Timeframe Button */}
        <button
          onClick={() => setShowTimeframeSelector(!showTimeframeSelector)}
          className={`${chipButtonClass} font-medium rounded-md border border-border bg-muted/50 hover:bg-muted transition-colors ${
            showTimeframeSelector ? "invisible" : ""
          }`}
        >
          {selectedTimeframe}
        </button>

        <div className={`w-px ${isDesktop ? "h-3" : "h-4"} bg-border`} />

        {/* Symbol Selector */}
        <KLineChartSymbolSelector isDesktop={isDesktop} />
      </div>

      {showTimeframeSelector ? (
        /* Timeframe selector overlay */
        <div
          className={`absolute inset-0 bg-background flex items-center ${
            isDesktop
              ? "px-2 gap-1"
              : "px-2 gap-1.5 overflow-x-auto overscroll-x-contain [scrollbar-width:none]"
          } border-t border-border`}
        >
          {/* Visible timeframes */}
          {visibleTimeframes.map((timeframe) => (
            <button
              key={timeframe}
              onClick={() => handleTimeframeSelect(timeframe)}
              className={`${chipButtonClass} shrink-0 font-medium rounded-md transition-colors ${
                selectedTimeframe === timeframe
                  ? "bg-primary text-primary-foreground"
                  : "bg-transparent hover:bg-muted text-foreground"
              }`}
            >
              {timeframe}
            </button>
          ))}

          {/* More button for mobile */}
          {!isDesktop && hiddenTimeframes.length > 0 && (
            <Popover
              open={moreTimeframesOpen}
              onOpenChange={setMoreTimeframesOpen}
            >
              <PopoverTrigger asChild>
                <button
                  className={`${chipButtonClass} font-medium rounded-md transition-colors bg-transparent hover:bg-muted text-foreground flex items-center gap-1`}
                >
                  <LuClock className={isDesktop ? "h-3 w-3" : "h-3.5 w-3.5"} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-40 p-2" align="start">
                <div className="grid gap-1">
                  {hiddenTimeframes.map((timeframe) => (
                    <Button
                      key={timeframe}
                      variant={
                        selectedTimeframe === timeframe ? "default" : "ghost"
                      }
                      size="sm"
                      onClick={() => handleTimeframeSelect(timeframe)}
                      className="justify-start h-8 text-sm font-medium"
                    >
                      {timeframe}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          <button
            onClick={() => setShowTimeframeSelector(false)}
            className={`ml-auto ${chipButtonClass} shrink-0 sticky right-0 bg-background hover:bg-muted rounded-md transition-colors`}
          >
            Done
          </button>
        </div>
      ) : (
        <>
          {/* Center - Main tools */}
          <div
            className={`flex items-center absolute left-1/2 transform -translate-x-1/2 ${
              isDesktop ? "gap-0.5" : "gap-1"
            }`}
          >
            <button
              type="button"
              role="switch"
              aria-checked={isCrosshairVisible}
              aria-label={
                isCrosshairVisible
                  ? "Disable crosshair mode"
                  : "Enable crosshair mode"
              }
              onClick={() => setIsCrosshairVisible(!isCrosshairVisible)}
              className={`${toolButtonClass} rounded-md flex items-center justify-center transition-colors ${
                isCrosshairVisible
                  ? isDesktop
                    ? "bg-primary/10 text-primary"
                    : "bg-primary/15 text-primary"
                  : "hover:bg-muted text-foreground"
              }`}
              title={
                isCrosshairVisible
                  ? "Disable crosshair mode (resume chart pan and zoom)"
                  : "Enable crosshair mode (lock chart pan and zoom)"
              }
            >
              <svg
                width={toolIconSize}
                height={toolIconSize}
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </button>

            {/* Indicators button - with popover for desktop */}
            {isDesktop && onIndicatorsPopoverChange ? (
              <Popover
                open={showIndicatorsPopover}
                onOpenChange={onIndicatorsPopoverChange}
              >
                <PopoverTrigger asChild>
                  <button
                    className={`${toolButtonClass} rounded-md flex items-center justify-center hover:bg-muted transition-colors`}
                    title="Chart indicators"
                  >
                    <PiFunction size={toolIconSize} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[480px] h-[min(640px,85vh)] p-0 overflow-hidden"
                  align="center"
                  side="bottom"
                  sideOffset={8}
                >
                  <IndicatorsScreen
                    onBack={handleIndicatorsBack}
                    isDesktop={isDesktop}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <button
                onClick={showIndicatorsScreen}
                className={`${toolButtonClass} rounded-md flex items-center justify-center hover:bg-muted transition-colors`}
                title="Chart indicators"
              >
                <PiFunction size={toolIconSize} />
              </button>
            )}

            {/* Objects button - with popover for desktop */}
            {isDesktop && onObjectsPopoverChange ? (
              <Popover
                open={showObjectsPopover}
                onOpenChange={onObjectsPopoverChange}
              >
                <PopoverTrigger asChild>
                  <button
                    className={`${toolButtonClass} rounded-md flex items-center justify-center hover:bg-muted transition-colors`}
                    title="Drawing tools"
                  >
                    <svg
                      width={toolIconSize}
                      height={toolIconSize}
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <rect
                        x="3"
                        y="3"
                        width="6"
                        height="6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <rect
                        x="11"
                        y="3"
                        width="6"
                        height="6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <rect
                        x="3"
                        y="11"
                        width="6"
                        height="6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <rect
                        x="11"
                        y="11"
                        width="6"
                        height="6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[480px] h-[min(640px,85vh)] p-0 overflow-hidden"
                  align="center"
                  side="bottom"
                  sideOffset={8}
                >
                  <DrawingsScreen
                    onBack={handleObjectsBack}
                    setSelectedDrawingType={handleObjectSelected}
                    isDesktop={isDesktop}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <button
                onClick={showObjectsScreen}
                className={`${toolButtonClass} rounded-md flex items-center justify-center hover:bg-muted transition-colors`}
                title="Drawing tools"
              >
                <svg
                  width={toolIconSize}
                  height={toolIconSize}
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <rect
                    x="3"
                    y="3"
                    width="6"
                    height="6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="11"
                    y="3"
                    width="6"
                    height="6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="3"
                    y="11"
                    width="6"
                    height="6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="11"
                    y="11"
                    width="6"
                    height="6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Right side - Trading actions */}
          <div
            className={`flex items-center ${isDesktop ? "gap-0.5" : "gap-1"}`}
          >
            {isDesktop && !isPopup ? (
              <button
                type="button"
                onClick={() => {
                  const slot = useChartStore3.getState().slots[chartPanelId];
                  const { selectedSymbol, selectedTimeframe: tf } = slot;
                  if (!selectedSymbol) return;
                  openChartPopup({
                    symbol: selectedSymbol.currency,
                    timeframe: tf,
                  });
                }}
                className={`${toolButtonClass} rounded-md flex items-center justify-center hover:bg-muted transition-colors text-foreground`}
                title="Pop out chart"
              >
                <LuSquareArrowOutUpRight size={toolIconSize} />
              </button>
            ) : null}

            <button
              type="button"
              disabled={!selectedSymbol}
              aria-pressed={isPendingPanelActive}
              onClick={() =>
                setIsSpecificTradePanelVisible(!isSpecificTradePanelVisible)
              }
              className={cn(
                toggleButtonClass,
                "rounded-md flex items-center justify-center transition-colors",
                !selectedSymbol && "cursor-not-allowed opacity-50",
                isPendingPanelActive
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-muted text-foreground",
              )}
              title="Pending orders"
            >
              <LuClock size={toggleIconSize} />
            </button>

            <button
              type="button"
              aria-pressed={isPanelVisible}
              onClick={() => setIsPanelVisible((prev: boolean) => !prev)}
              className={cn(
                toggleButtonClass,
                "rounded-md flex items-center justify-center transition-colors",
                isPanelVisible
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-muted text-foreground",
              )}
              title="Toggle trading panel"
            >
              <LuPanelTop size={toggleIconSize} />
            </button>

            <button
              type="button"
              aria-pressed={isMarketDepthVisible}
              onClick={() => setIsMarketDepthVisible((prev: boolean) => !prev)}
              className={cn(
                toggleButtonClass,
                "rounded-md flex items-center justify-center transition-colors",
                isMarketDepthVisible
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-muted text-foreground",
              )}
              title="Toggle market depths"
            >
              <LuLayers size={toggleIconSize} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default memo(ChartHeader);
